import { ScopeOfWorkState, ScopeOfWorkStep } from "../context/scope-of-work.types";

/** Commercial flows often have no catalog "main" areas; users only add other areas. */
const hasMainOrOtherAreas = (state: ScopeOfWorkState): boolean => {
  const main = Array.isArray(state.areas) && state.areas.length > 0;
  const other = Array.isArray(state.otherAreas) && state.otherAreas.length > 0;
  return main || other;
};

/**
 * Validates the CURRENT step of the internal wizard step by step
 * Used in ScopeOfWorkController.next() to prevent incomplete steps
 */
export const scopeOfWorkStepValidator = (state: ScopeOfWorkState): true | string => {
  if (!state) return "Invalid state";

  const step = state.step;

  // STEP 1: Category
  if (step === ScopeOfWorkStep.Categories) {
    if (!state.category?.uid) {
      return "Please select a category";
    }
    return true;
  }

  // STEP 2: Type of Places
  if (step === ScopeOfWorkStep.TypeOfPlaces) {
    // Verify that the previous step was completed
    if (!state.category?.uid) {
      return "You must first select a category";
    }
    if (!state.places?.uid) {
      return "Please select a type of place";
    }
    if (!state.squareft) {
      return "Please enter the square footage";
    }
    return true;
  }

  // STEP 3: Type of Service
  if (step === ScopeOfWorkStep.TypeOfService) {
    if (!state.category?.uid) {
      return "Category is required";
    }
    if (!state.places?.uid) {
      return "Type of place is required";
    }
    if (!state.service?.uid) {
      return "Please select a type of service";
    }
    return true;
  }

  // STEP 4: Areas
  if (step === ScopeOfWorkStep.Areas) {
    if (!state.category?.uid) {
      return "Category is required";
    }
    if (!state.places?.uid) {
      return "Type of place is required";
    }
    if (!state.service?.uid) {
      return "Type of service is required";
    }
    if (!hasMainOrOtherAreas(state)) {
      return "Please add at least one area";
    }
    return true;
  }

  // STEP 5: Objects (optional, but if any, must be valid)
  if (step === ScopeOfWorkStep.Objects) {
    if (!state.category?.uid) {
      return "Category is required";
    }
    if (!state.places?.uid) {
      return "Type of place is required";
    }
    if (!state.service?.uid) {
      return "Type of service is required";
    }
    if (!hasMainOrOtherAreas(state)) {
      return "Areas are required";
    }
    // Objects are optional
    return true;
  }

  // STEP 6: Special Requests (optional)
  if (step === ScopeOfWorkStep.SpecialRequests) {
    if (!state.category?.uid) {
      return "Category is required";
    }
    if (!state.places?.uid) {
      return "Type of place is required";
    }
    if (!state.service?.uid) {
      return "Type of service is required";
    }
    if (!hasMainOrOtherAreas(state)) {
      return "Areas are required";
    }
    // Special requests are optional
    return true;
  }

  // STEP 7: Summary (optional)
  if (step === ScopeOfWorkStep.Summary) {
    return true;
  }

  return "Invalid step";
};

/**
 * Validates that the entire ScopeOfWork is complete when exiting the wizard
 * Used in JobNextPrev to prevent exiting the wizard incompletely
 */
export const scopeOfWorkCompleteValidator = (state: any): true | string => {
  if (!state) {
    return "The Scope of Work does not exist";
  }

  // Final validation: all required fields present
  if (!state.category?.uid) {
    return "Category selection is required";
  }

  if (!state.places?.uid) {
    return "Type of place selection is required";
  }

  if (!state.squareft) {
    return "Square footage is required";
  }

  if (!state.service?.uid) {
    return "Service type selection is required";
  }

  if (!hasMainOrOtherAreas(state)) {
    return "At least one area must be added";
  }

  // objects and specialRequests are optional

  return true;
};
